import { useEffect, useRef } from "react"
import { type NavigateFunction, useNavigate } from "react-router-dom"

import { ROLE_IDS } from "../constants/roles.ts"
import { RoutesPaths } from "../constants/routes.ts"
import { type AuthState } from "../containers/LoginPage/redux/auth.type.ts"
import { isSharedFilePath } from "../containers/SharedFilePage/shared-file-path.ts"
import {
  type HandledErrorAction,
  createHandledError
} from "../errors/handled-error"
import {
  reportHandledError,
  reportUnexpectedFailure
} from "../observability/handled-errors"
import { errorSave } from "../redux/errors.slice"
import API, { request } from "../services/api"
import { type AppDispatch } from "../store.ts"
import utils from "../utils/utils"
import { useAppDispatch } from "./use-app-dispatch.ts"

// Kept next to the route itself in constants/routes, so the guard and the router
// cannot disagree about what the recipient's path is.
const SIGNING_PATH = RoutesPaths.signing
const CUSTOMER_UPLOAD_PATH = RoutesPaths.customerUpload

const registrationPages = [
  "sign-up",
  "add-email",
  "restore-password",
  "check-mailbox",
  "verify-code",
  "success",
  "auth",
  "confirm-email-change",
  "whatsapp_login"
]

const innerPages = [
  "home",
  "dashboard",
  "reports",
  "invoices",
  "leads",
  "calendar",
  "projects",
  "customers",
  "subcontractors",
  "suppliers",
  "team_management",
  "settings",
  "confirm-email-change",
  "whatsapp_login",
  "available-time",
  "templates",
  "proposal",
  "proposals",
  "contract",
  "contracts",
  "document_templates",
  "proposal_templates",
  "contract_templates",
  "document_reviews",
  "guide",
  "portal",
  "map"
]

interface StartupAuthState {
  accessToken: AuthState["accessToken"]
  refreshToken: AuthState["refreshToken"]
  userInfo: AuthState["userInfo"] | null
}

interface RunInitialRouteGuardParams {
  auth: StartupAuthState
  dispatch: AppDispatch
  navigate: NavigateFunction
  pathname?: string
  search?: string
}

interface OAuthRedirectResponse {
  redirect_uri?: unknown
  error?: unknown
}

interface ContinueOAuthParams {
  nextUrl: string
  dispatch: AppDispatch
  navigate: NavigateFunction
  retryHref: string
}

export const OAUTH_CONTINUATION_FLOW = "startup.oauth-continuation"
export const STARTUP_GUARD_FLOW = "startup.route-guard"

const OAUTH_USER_CANCELLED = "access_denied"

const includesCurrentPathSegment = (pathname: string, segment: string) =>
  utils.includeCurrentLocation(pathname, segment)

const getNextUrl = (search: string) =>
  search?.includes("?next=") ? search?.split("?next=")?.[1] : null

const decodeNextUrl = (nextUrl: string) => {
  try {
    return decodeURIComponent(nextUrl)
  } catch (e) {
    console.error("Error decoding nextUrl:", e)
    return nextUrl
  }
}

const dispatchGoLink = (dispatch: AppDispatch, link: string) => {
  ;(dispatch as (action: unknown) => unknown)(utils.goLink(link))
}

const isUserCancellation = (nextUrl: string, data: unknown): boolean =>
  nextUrl.includes(`error=${OAUTH_USER_CANCELLED}`) ||
  (typeof data === "object" &&
    data !== null &&
    (data as OAuthRedirectResponse).error === OAUTH_USER_CANCELLED)

const isRedirectTarget = (value: unknown): value is string =>
  typeof value === "string" && /^https?:\/\//i.test(value)

const oauthAction = (
  status: number | null,
  retryHref: string
): HandledErrorAction =>
  status === 401 || status === 403
    ? { kind: "restart-login", href: RoutesPaths.auth.login }
    : { kind: "retry", href: retryHref }

const continueOAuth = async ({
  nextUrl,
  dispatch,
  navigate,
  retryHref
}: ContinueOAuthParams): Promise<void> => {
  const response = await request.api.post<OAuthRedirectResponse>(nextUrl, {
    maxRedirects: 0,
    validateStatus: (status: number) => status >= 200 && status < 400
  })

  if (
    response?.problem === "CANCEL_ERROR" ||
    isUserCancellation(nextUrl, response?.data)
  ) {
    navigate(RoutesPaths.home, { replace: true })
    return
  }

  const redirectUri = response?.data?.redirect_uri
  if (response?.ok && isRedirectTarget(redirectUri)) {
    dispatchGoLink(dispatch, redirectUri)
    return
  }

  const status = typeof response?.status === "number" ? response.status : null
  const error = createHandledError(response ?? {}, {
    flow: OAUTH_CONTINUATION_FLOW,
    endpoint: nextUrl,
    method: "post",
    code: response?.ok ? "malformed" : "oauth_continuation",
    action: oauthAction(status, retryHref)
  })
  reportHandledError(error)
  dispatch(errorSave(error))
  navigate(RoutesPaths.home, { replace: true })
}

const runRouteGuard = async ({
  auth,
  dispatch,
  navigate,
  pathname,
  search
}: Required<RunInitialRouteGuardParams>): Promise<boolean> => {
  const { accessToken, refreshToken, userInfo } = auth

  // The signing page belongs to someone with no account here, and its token
  // lives in the URL fragment. Every branch below navigates, and navigating
  // drops the fragment: a signed-in contractor opening a link would be sent to
  // /home, and everyone else would arrive with the token already gone.
  //
  // Matched exactly rather than by prefix: "/sign-up" starts with "/sign", and
  // a prefix test hands the whole registration flow the same exemption.
  if (
    pathname === SIGNING_PATH ||
    pathname.startsWith(`${SIGNING_PATH}/`) ||
    pathname === CUSTOMER_UPLOAD_PATH
  ) {
    return true
  }

  // A share link reaches people with no account, and signed-in ones from
  // another company; innerPages would send the first kind to /login.
  if (isSharedFilePath(pathname)) {
    return true
  }

  if (accessToken) API.updateAccessToken(accessToken, refreshToken)

  if (accessToken && userInfo) {
    if (
      includesCurrentPathSegment(pathname, "sign-up") &&
      pathname !== "/sign-up"
    ) {
      return true
    }

    // An invitation link is a legitimate destination for an already-logged-in
    // account too — a customer accepting a SECOND GC's invite is exactly this
    // case (SignUpPage itself detects `userInfo` + `invite` and accepts with
    // no form, proving identity by session rather than a password). Without
    // this, every authenticated visit to /sign-up?invite=... below bounced
    // straight to the account's own home, silently discarding the invite.
    if (pathname === "/sign-up" && /(?:^|[?&])invite=/.test(search)) {
      return true
    }

    if (
      pathname !== "/" &&
      innerPages.some((page) => includesCurrentPathSegment(pathname, page))
    ) {
      const nextUrl = getNextUrl(search)

      if (nextUrl) {
        await continueOAuth({
          nextUrl: decodeNextUrl(nextUrl),
          dispatch,
          navigate,
          retryHref: `${pathname}${search || ""}`
        })
      }

      return true
    }

    if (userInfo["email_added"]) {
      const landing =
        userInfo.role?.id === ROLE_IDS.CUSTOMER
          ? RoutesPaths.portal.home
          : RoutesPaths.home
      navigate(landing + (search || ""))
    } else {
      navigate("/add-email")
    }

    return true
  }

  if (
    registrationPages.some((page) => includesCurrentPathSegment(pathname, page))
  ) {
    if (pathname === "/sign-up") {
      navigate(`/sign-up${search || ""}`)
    }

    return true
  }

  if (
    !["/", "/login"]?.includes(pathname) &&
    innerPages.some((page) => includesCurrentPathSegment(pathname, page))
  ) {
    navigate("/login", { replace: true })
    return true
  }

  const url =
    pathname === "/" ? "/login" + (search || "") : pathname + (search || "")
  navigate(url, { replace: true })

  return true
}

export const runInitialRouteGuard = async ({
  auth,
  dispatch,
  navigate,
  pathname = document.location.pathname,
  search = document.location.search
}: RunInitialRouteGuardParams): Promise<boolean> => {
  try {
    return await runRouteGuard({ auth, dispatch, navigate, pathname, search })
  } catch (failure) {
    const nextUrl = getNextUrl(search)
    reportUnexpectedFailure(failure, {
      flow: STARTUP_GUARD_FLOW,
      endpoint: nextUrl ? decodeNextUrl(nextUrl) : pathname
    })
    dispatch(
      errorSave(
        createHandledError(
          {},
          {
            flow: STARTUP_GUARD_FLOW,
            endpoint: pathname,
            code: "unknown",
            action: { kind: "retry", href: `${pathname}${search || ""}` }
          }
        )
      )
    )
    navigate(RoutesPaths.home, { replace: true })
    return false
  }
}

export const useStartup = (auth: StartupAuthState) => {
  const hasRunRef = useRef(false)
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  useEffect(() => {
    if (hasRunRef.current) return
    hasRunRef.current = true

    void runInitialRouteGuard({ auth, dispatch, navigate })
  }, [auth, dispatch, navigate])
}
